import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Plus, Trash, PencilSimple, FileXls, FilePdf, ArrowsClockwise, BookOpen } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useSearch } from '../components/VerticalTabs';
import { InvoiceDetail } from '../components/invoicing/InvoiceDetail';
import { QuotePercentageDialog } from '../components/invoicing/QuotePercentageDialog';
import { InvoiceFormModal, LibraryModal } from '../components/invoicing/InvoiceFormModal';
import { QuoteSelectorModal } from '../components/invoicing/QuoteSelectorModal';
import { InvoiceSettings } from '../components/invoicing/InvoiceSettings';
import { PdfViewerModal } from '../components/invoicing/PdfViewerModal';
import { ExcelImportButton } from '../components/shared/ExcelImportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' },
  acceptee: { label: 'Acceptee', color: '#92400E', bg: '#FEF3C7' },
  emise: { label: 'Emise', color: '#1565C0', bg: '#E3F2FD' },
  payee: { label: 'Payee', color: '#2E7D32', bg: '#E8F5E9' },
  annulee: { label: 'Annulee', color: '#C62828', bg: '#FFEBEE' },
  accepte: { label: 'Accepte', color: '#2E7D32', bg: '#E8F5E9' },
  refuse: { label: 'Refuse', color: '#C62828', bg: '#FFEBEE' },
};

const InvoicingPage = ({ embedded }) => {
  const [activeMainTab, setActiveMainTab] = useState('factures');
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState('facture');
  const [filterProject, setFilterProject] = useState('all');
  const [libForm, setLibForm] = useState({ description: '', details: '', unit: '', unit_price: 0, tva_rate: 20 });
  const [editLibId, setEditLibId] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState({});
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: '', title: '' });
  const [previousAdvancements, setPreviousAdvancements] = useState({});
  const [form, setForm] = useState({
    project_id: '', client_id: '', payment_delay: 30, date: new Date().toISOString().slice(0, 10), validity_date: '', due_date: '', notes: '',
    client_name: '', client_email: '', client_phone: '', client_address: '',
    lines: [{ description: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }]
  });

  const fetchData = useCallback(async () => {
    try {
      const [invRes, quotesRes, projRes, libRes, settingsRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoicing`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/quotes`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/library`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/settings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/contacts?category=client`, { withCredentials: true }),
      ]);
      setInvoices(invRes.data);
      setQuotes(quotesRes.data);
      setProjects(projRes.data);
      setLibrary(libRes.data);
      setInvoiceSettings(settingsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // React to URL params: ?id={invoiceId}&edit=1 opens invoice in edit mode.
  // ?id={invoiceId} alone opens it for consultation. Parses once data is loaded.
  const location = useLocation();
  const navigate = useNavigate();
  // Honor ?projectId=<id> from external links (e.g. dashboard "Ready to bill"
  // widget) so the user lands directly on the project's invoices.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wantedProject = params.get('projectId');
    if (wantedProject && wantedProject !== filterProject) {
      setFilterProject(wantedProject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  useEffect(() => {
    if (loading || !invoices.length) return;
    const params = new URLSearchParams(location.search);
    const wantedId = params.get('id');
    const wantEdit = params.get('edit') === '1';
    if (!wantedId) return;
    const inv = invoices.find(i => i.id === wantedId);
    if (!inv) return;
    if (wantEdit) {
      openEdit(inv, 'facture');
    } else {
      setSelectedItem({ ...inv, _type: 'facture' });
    }
    // Clear params to avoid re-triggering on subsequent renders
    const newParams = new URLSearchParams(location.search);
    newParams.delete('id');
    newParams.delete('edit');
    const qs = newParams.toString();
    navigate(`${location.pathname}${qs ? '?' + qs : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invoices.length, location.search]);

  const computeDueDate = (baseDate, delay) => {
    if (!baseDate) return '';
    const d = new Date(baseDate);
    d.setDate(d.getDate() + Number(delay || 0));
    return d.toISOString().slice(0, 10);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validLines = form.lines.filter(ln => ln.description.trim());
    if (validLines.length === 0) return toast.error('Ajoutez au moins un poste');
    
    // Validation specifique devis : client obligatoire
    if (modalType === 'devis' && !form.client_id) {
      return toast.error('Selectionnez un client dans le repertoire des contacts');
    }
    // Validation facture : date echeance obligatoire
    let dueDate = form.due_date;
    if (modalType === 'facture') {
      if (!dueDate && form.payment_delay && form.payment_delay !== 'custom') {
        dueDate = computeDueDate(form.date, form.payment_delay);
      }
      if (!dueDate) return toast.error('La date d\'echeance est obligatoire');
    }
    
    const basePayload = {
      project_id: form.project_id || null,
      client_id: form.client_id || null,
      date: form.date,
      notes: form.notes,
      client_name: form.client_name,
      client_email: form.client_email,
      client_phone: form.client_phone,
      client_address: form.client_address,
      lines: validLines,
    };
    try {
      if (modalType === 'devis') {
        const payload = { ...basePayload, validity_date: form.validity_date };
        if (editingItem) {
          await axios.put(`${API_URL}/api/invoicing/quotes/${editingItem.id}`, payload, { withCredentials: true });
          toast.success('Devis mis a jour');
        } else {
          await axios.post(`${API_URL}/api/invoicing/quotes`, payload, { withCredentials: true });
          toast.success('Devis cree');
        }
      } else {
        const payload = { ...basePayload, due_date: dueDate };
        if (editingItem) {
          await axios.put(`${API_URL}/api/invoicing/${editingItem.id}`, payload, { withCredentials: true });
          toast.success('Facture mise a jour');
        } else {
          await axios.post(`${API_URL}/api/invoicing`, payload, { withCredentials: true });
          toast.success('Facture creee');
        }
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateStatus = async (id, newStatus, type = 'facture') => {
    try {
      const url = type === 'devis' ? `${API_URL}/api/invoicing/quotes/${id}` : `${API_URL}/api/invoicing/${id}`;
      await axios.put(url, { status: newStatus }, { withCredentials: true });
      toast.success('Statut mis a jour');
      fetchData();
      if (selectedItem?.id === id) setSelectedItem(prev => ({ ...prev, status: newStatus }));
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async (id, type = 'facture') => {
    // Force delete : on confirme, on supprime, même si "validé / payé / émis".
    const target = type === 'devis' ? 'ce devis' : 'cette facture';
    if (!window.confirm(`Supprimer ${target} ? Cette action est définitive et contourne les blocages habituels.`)) return;
    try {
      const url = type === 'devis' ? `${API_URL}/api/invoicing/quotes/${id}` : `${API_URL}/api/invoicing/${id}`;
      await axios.delete(url, { withCredentials: true });
      toast.success('Supprime');
      if (selectedItem?.id === id) setSelectedItem(null);
      fetchData();
    } catch (error) { toast.error(error?.response?.data?.detail || 'Erreur'); }
  };

  const [convertDialog, setConvertDialog] = useState({ open: false, quote: null, submitting: false, previousAdvancements: {} });
  const openConvertDialog = async (quote) => {
    let prev = {};
    try {
      const r = await axios.get(`${API_URL}/api/invoicing/quotes/${quote.id}/advancements`, { withCredentials: true });
      prev = r.data?.previous_advancements || {};
    } catch { /* fallback */ }
    setConvertDialog({ open: true, quote, submitting: false, previousAdvancements: prev });
  };
  const handleConvertConfirm = async (lineAdvancements) => {
    const q = convertDialog.quote;
    if (!q) return;
    setConvertDialog(d => ({ ...d, submitting: true }));
    try {
      const res = await axios.post(
        `${API_URL}/api/invoicing/quotes/${q.id}/convert`,
        { line_advancements: lineAdvancements },
        { withCredentials: true }
      );
      toast.success(`Facture ${res.data.number} creee depuis le devis`);
      setConvertDialog({ open: false, quote: null, submitting: false, previousAdvancements: {} });
      fetchData();
      setSelectedItem(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la conversion');
      setConvertDialog(d => ({ ...d, submitting: false }));
    }
  };
  const handleConvertQuote = async (quoteId) => {
    const q = quotes.find(x => x.id === quoteId);
    if (q) await openConvertDialog(q);
  };

  const handleCreateCreditNote = async (invoiceId, reason) => {
    try {
      const r = await axios.post(`${API_URL}/api/invoicing/${invoiceId}/credit-note`, { reason, partial: false }, { withCredentials: true });
      toast.success(`Avoir ${r.data.number} créé (${(r.data.total_ttc || 0).toLocaleString('fr-FR')} € TTC)`);
      await fetchData();
      setSelectedItem(null);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création avoir');
      return null;
    }
  };

  const handleExportPdf = async (id, type = 'facture') => {
    const url = type === 'devis' ? `${API_URL}/api/invoicing/quotes/${id}/export/pdf` : `${API_URL}/api/invoicing/${id}/export/pdf`;
    try {
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const item = type === 'devis' ? quotes.find(q => q.id === id) : invoices.find(i => i.id === id);
      setPdfViewer({ show: true, url: blobUrl, title: item ? `${type === 'devis' ? 'Devis' : 'Facture'} ${item.number}` : 'Document PDF' });
    } catch (err) {
      toast.error('Impossible de charger le PDF');
    }
  };

  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: '', title: '' });
  };

  const handleExportExcel = (id) => { window.open(`${API_URL}/api/invoicing/${id}/export/excel`, '_blank'); };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/invoicing/settings`, invoiceSettings, { withCredentials: true });
      toast.success('Parametres sauvegardes');
    } catch (error) { toast.error('Erreur'); }
  };

  const handleSaveLibItem = async (e) => {
    e.preventDefault();
    try {
      if (editLibId) await axios.put(`${API_URL}/api/invoicing/library/${editLibId}`, libForm, { withCredentials: true });
      else await axios.post(`${API_URL}/api/invoicing/library`, libForm, { withCredentials: true });
      toast.success(editLibId ? 'Poste mis a jour' : 'Poste ajoute');
      setLibForm({ description: '', details: '', unit: '', unit_price: 0, tva_rate: 20 });
      setEditLibId(null);
      fetchData();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDeleteLibItem = async (id) => {
    try { await axios.delete(`${API_URL}/api/invoicing/library/${id}`, { withCredentials: true }); toast.success('Poste supprime'); fetchData(); } catch (error) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setEditingItem(null);
    setForm({ project_id: '', client_id: '', payment_delay: 30, date: new Date().toISOString().slice(0, 10), validity_date: '', due_date: '', notes: '',
      client_name: '', client_email: '', client_phone: '', client_address: '',
      lines: [{ description: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }] });
  };

  const openCreateModal = (type) => { setModalType(type); setEditingItem(null); resetForm(); setShowModal(true); };

  // Invoice creation requires a quote first: open selector
  const [showQuoteSelector, setShowQuoteSelector] = useState(false);
  const handleCreateInvoiceFlow = () => setShowQuoteSelector(true);
  const handlePickQuoteForInvoice = async (quote) => {
    setShowQuoteSelector(false);
    await openConvertDialog(quote);
  };
  const handleCreateNewQuoteFromInvoiceFlow = () => {
    setShowQuoteSelector(false);
    openCreateModal('devis');
    toast.info('Creez d\'abord le devis. Vous pourrez ensuite le facturer.');
  };

  const openEdit = (item, type) => {
    setModalType(type);
    setEditingItem(item);
    setForm({
      project_id: item.project_id || '', client_id: item.client_id || '',
      payment_delay: 'custom', // when editing, keep manual due_date
      date: item.date, validity_date: item.validity_date || '', due_date: item.due_date || '', notes: item.notes || '',
      client_name: item.client_name || '', client_email: item.client_email || '', client_phone: item.client_phone || '', client_address: item.client_address || '',
      lines: item.lines.map(ln => ({ id: ln.id, kind: ln.kind || 'item', line_id: ln.line_id, source_line_id: ln.source_line_id, previous_advancement: ln.previous_advancement, description: ln.description, details: ln.details || '', unit: ln.unit, unit_price: ln.unit_price, quantity: ln.quantity, tva_rate: ln.tva_rate, monthly_advancement: ln.monthly_advancement || 0, total_advancement: ln.total_advancement || 0 }))
    });
    // Fetch previous advancements when this invoice is linked to a quote
    if (type === 'facture' && item.quote_id) {
      axios.get(`${API_URL}/api/invoicing/quotes/${item.quote_id}/advancements`, {
        params: { exclude_invoice_id: item.id },
        withCredentials: true,
      }).then(r => {
        setPreviousAdvancements(r.data?.previous_advancements || {});
      }).catch(() => setPreviousAdvancements({}));
    } else {
      setPreviousAdvancements({});
    }
    setShowModal(true);
  };

  const addLine = () => { setForm({ ...form, lines: [...form.lines, { description: '', details: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }] }); };
  const addFromLibrary = (libItem) => { setForm({ ...form, lines: [...form.lines, { description: libItem.description, unit: libItem.unit, unit_price: libItem.unit_price, quantity: 0, tva_rate: libItem.tva_rate, monthly_advancement: 0, total_advancement: 0 }] }); toast.success(`"${libItem.description}" ajoute`); };
  const quickAddToLibrary = async (line) => {
    if (!line.description || !line.description.trim()) return;
    try {
      const payload = {
        description: line.description.trim(),
        unit: line.unit || '',
        unit_price: Number(line.unit_price) || 0,
        tva_rate: Number(line.tva_rate) || 20,
      };
      const res = await axios.post(`${API_URL}/api/invoicing/library`, payload, { withCredentials: true });
      const newItem = { id: res.data?.id, ...payload };
      setLibrary(prev => [...prev, newItem]);
      toast.success(`Poste "${payload.description}" ajoute a la bibliotheque`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur ajout bibliotheque');
    }
  };
  const updateLine = (idx, field, value) => { const newLines = [...form.lines]; newLines[idx] = { ...newLines[idx], [field]: value }; setForm({ ...form, lines: newLines }); };
  const removeLine = (idx) => { if (form.lines.length <= 1) return; setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) }); };

  const calcLineHT = (ln) => (ln.unit_price || 0) * (ln.quantity || 0);
  const calcLineTTC = (ln) => calcLineHT(ln) * (1 + (ln.tva_rate || 20) / 100);
  const calcAdvHT = (ln) => calcLineHT(ln) * (ln.total_advancement || 0) / 100;

  const statusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.brouillon;
    return <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
  };

  const { searchTerm } = useSearch();

  const filtered = useMemo(() => {
    let base;
    if (filterProject === 'all') base = invoices;
    else if (filterProject === 'none') base = invoices.filter(i => !i.project_id);
    else base = invoices.filter(i => i.project_id === filterProject);
    return base.filter(i => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return (i.number || '').toLowerCase().includes(s) || (i.project_name || '').toLowerCase().includes(s) || (i.client_name || '').toLowerCase().includes(s);
    });
  }, [invoices, filterProject, searchTerm]);

  const filteredQuotes = useMemo(() => {
    let base;
    if (filterProject === 'all') base = quotes;
    else if (filterProject === 'none') base = quotes.filter(q => !q.project_id);
    else base = quotes.filter(q => q.project_id === filterProject);
    return base.filter(q => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return (q.number || '').toLowerCase().includes(s) || (q.project_name || '').toLowerCase().includes(s) || (q.client_name || '').toLowerCase().includes(s);
    });
  }, [quotes, filterProject, searchTerm]);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  if (selectedItem) {
    return <InvoiceDetail item={selectedItem} statusBadge={statusBadge} onBack={() => setSelectedItem(null)} onExportPdf={handleExportPdf} onExportExcel={handleExportExcel} onEdit={openEdit} onDelete={handleDelete} onUpdateStatus={handleUpdateStatus} onConvertQuote={handleConvertQuote} onCreateCreditNote={handleCreateCreditNote} invoices={invoices} fetchData={fetchData} setSelectedItem={setSelectedItem} />;
  }

  const renderTable = (items, type) => {
    const isQuote = type === 'devis';
    if (items.length === 0) {
      return (<div className="card"><div className="empty-state"><FileText size={48} className="empty-state-icon" /><p className="empty-state-text">Aucun {isQuote ? 'devis' : 'facture'}</p></div></div>);
    }
    return (
      <div className="card">
        <table className="data-table" data-testid={`${type}-table`}>
          <thead>
            <tr>
              <th>N {isQuote ? 'Devis' : 'Facture'}</th>
              <th>Chantier</th><th>Date</th><th>Postes</th>
              <th style={{ textAlign: 'right' }}>Total HT</th>
              <th style={{ textAlign: 'right' }}>Total TTC</th>
              <th>Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedItem({ ...item, _viewType: type })}>
                <td style={{ fontWeight: '700', color: '#002FA7' }}>{item.number}</td>
                <td>{item.project_name || <span style={{ color: '#9E9EA7', fontStyle: 'italic' }}>Hors chantier</span>}</td><td>{item.date}</td><td>{item.lines.length}</td>
                <td style={{ textAlign: 'right', fontWeight: '600' }}>{item.total_ht.toLocaleString('fr-FR')} EUR</td>
                <td style={{ textAlign: 'right', fontWeight: '700', color: '#2E7D32' }}>{item.total_ttc.toLocaleString('fr-FR')} EUR</td>
                <td>{statusBadge(item.status)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleExportPdf(item.id, type)}><FilePdf size={14} /></button>
                    {isQuote && item.status !== 'refuse' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => openConvertDialog(item)}
                        title="Creer une facture en choisissant un % d'avancement par article"
                        data-testid={`convert-quote-row-${item.id}`}
                      >
                        <ArrowsClockwise size={14} />
                      </button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(item, type)}><PencilSimple size={14} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id, type)}><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div data-testid="invoicing-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Facturation</h1>
          <p className="page-subtitle">{invoices.length} facture(s) - {quotes.length} devis - {library.length} poste(s) en bibliotheque</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => setShowLibrary(true)} data-testid="open-library-btn"><BookOpen size={16} /> Bibliotheque</button>
          <button className="btn btn-secondary" onClick={() => openCreateModal('devis')} data-testid="create-quote-btn"><Plus size={16} /> Nouveau devis</button>
          <ExcelImportButton kind="factures" label="Importer" testid="invoicing-" onImported={() => fetchData()} />
          <button className="btn btn-primary" onClick={handleCreateInvoiceFlow} data-testid="create-invoice-btn"><Plus size={18} weight="bold" /> Nouvelle facture</button>
        </div>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <p style={{ color: '#5E5F66', fontSize: '0.9rem', margin: 0, alignSelf: 'center' }}>{invoices.length} facture(s) - {quotes.length} devis - {library.length} poste(s)</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={() => setShowLibrary(true)} data-testid="open-library-btn"><BookOpen size={16} /> Bibliotheque</button>
            <button className="btn btn-secondary" onClick={() => openCreateModal('devis')} data-testid="create-quote-btn"><Plus size={16} /> Devis</button>
            <button className="btn btn-primary" onClick={handleCreateInvoiceFlow} data-testid="create-invoice-btn"><Plus size={18} weight="bold" /> Facture</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '20px' }}>
        {[{ key: 'factures', label: `Factures (${invoices.length})` }, { key: 'devis', label: `Devis (${quotes.length})` }, { key: 'parametres', label: 'Parametres' }].map(tab => (
          <button key={tab.key} onClick={() => setActiveMainTab(tab.key)} data-testid={`tab-${tab.key}`}
            style={{ padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', backgroundColor: 'transparent', borderBottom: activeMainTab === tab.key ? '2px solid #002FA7' : '2px solid transparent', color: activeMainTab === tab.key ? '#002FA7' : '#5E5F66', marginBottom: '-2px' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeMainTab !== 'parametres' && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger style={{ width: '250px' }} data-testid="filter-project"><SelectValue placeholder="Filtrer par chantier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les chantiers</SelectItem>
              <SelectItem value="none">Sans chantier</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {activeMainTab === 'factures' && (
            <button
              data-testid="export-all-invoices-excel-btn"
              className="btn btn-secondary"
              onClick={() => window.open(`${API_URL}/api/invoicing/export/all-excel`, '_blank')}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FileXls size={16} weight="bold" /> Exporter toutes les factures (Excel)
            </button>
          )}
          {activeMainTab === 'devis' && (
            <button
              data-testid="export-all-quotes-excel-btn"
              className="btn btn-secondary"
              onClick={() => window.open(`${API_URL}/api/invoicing/quotes/export/all-excel`, '_blank')}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FileXls size={16} weight="bold" /> Exporter tous les devis (Excel)
            </button>
          )}
        </div>
      )}

      {activeMainTab === 'factures' && renderTable(filtered, 'facture')}
      {activeMainTab === 'devis' && renderTable(filteredQuotes, 'devis')}
      {activeMainTab === 'parametres' && <InvoiceSettings invoiceSettings={invoiceSettings} setInvoiceSettings={setInvoiceSettings} onSave={handleSaveSettings} />}

      <InvoiceFormModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSubmit} form={form} setForm={setForm} projects={projects} clients={clients} library={library} modalType={modalType} editingItem={editingItem} addLine={addLine} removeLine={removeLine} updateLine={updateLine} addFromLibrary={addFromLibrary} quickAddToLibrary={quickAddToLibrary} calcLineHT={calcLineHT} calcLineTTC={calcLineTTC} calcAdvHT={calcAdvHT} forceTvaZero={!!projects.find(p => p.id === form.project_id)?.autoliquidation_tva} onLibraryChanged={fetchData} previousAdvancements={previousAdvancements} />
      <LibraryModal show={showLibrary} onClose={() => setShowLibrary(false)} library={library} libForm={libForm} setLibForm={setLibForm} editLibId={editLibId} setEditLibId={setEditLibId} onSave={handleSaveLibItem} onDelete={handleDeleteLibItem} onRefresh={fetchData} />
      <QuoteSelectorModal
        show={showQuoteSelector}
        onClose={() => setShowQuoteSelector(false)}
        onPickQuote={handlePickQuoteForInvoice}
        onCreateNewQuote={handleCreateNewQuoteFromInvoiceFlow}
      />
      <PdfViewerModal show={pdfViewer.show} url={pdfViewer.url} title={pdfViewer.title} onClose={closePdfViewer} />
      {convertDialog.open && convertDialog.quote && (
        <QuotePercentageDialog
          quote={convertDialog.quote}
          previousAdvancements={convertDialog.previousAdvancements}
          onClose={() => setConvertDialog({ open: false, quote: null, submitting: false, previousAdvancements: {} })}
          onConfirm={handleConvertConfirm}
          submitting={convertDialog.submitting}
        />
      )}
    </div>
  );
};

export default InvoicingPage;
